/**
 * Device configuration types for frontend
 */



export interface PIGains {
  Kp: number;
  Ki: number;
}

export interface KalmanConfig {
  input_window_size: number;
}

export interface ControllerConfig {
  type: 'kalman' | 'pi';
  pi_gains: PIGains;
  kalman_config: KalmanConfig;
}

export interface DeviceConfigBase {
  // OCXO Control Configuration
  ocxo_control_type: 'SPI' | 'SSH';
  // PPS offset in nanoseconds; negative = anticipation (advance), positive = delay
  pps_offset_ns: number;
  // OCXO control baseline (DAC code, range 0..1048575)
  ocxo_control_baseline: number;
  
  // Device Configuration
  steering_mode: 'external_reference' | 'single_channel_gnss' | 'multi_channel_gnss' | 'adsb' | 'gnss_adsb' | 'open_loop';
  // is_adsb_enabled is now automatically calculated based on steering_mode (not user-configurable)
  num_slots: number;
  level: number; // 0..2
  // L0/L1 device ids this L2 does not use as ADS-B master (absent in configs saved before levels)
  adsb_excluded_master_ids?: number[];
  // GNSS antenna coordinates
  latitude_gnss_ant: number;
  longitude_gnss_ant: number;
  height_gnss_ant_m: number;
  // ADS-B antenna coordinates
  latitude_adsb_ant: number;
  longitude_adsb_ant: number;
  height_adsb_ant_m: number;
  // Open Loop command (used when steering_mode == 'open_loop')
  openloop_command: number;
  
  // GNSS Configuration
  gnss_count: number;
  gnss_internal_delays: number[];
  gnss_cable_delays: number[];
  
  // ADS-B Configuration
  adsb_internal_delay: number;
  adsb_cable_delay: number;
  
  // External Reference Configuration
  ext_ref_internal_delay: number;
  ext_ref_cable_delay: number;

  // CGGTTS: steering flag and calibration delays (ns), one entry per receiver
  kalman_cggtts_mode: boolean;
  cggtts_ref_dly: number;
  cggtts_cab_dly: number[];
  cggtts_int_dly: number[][];
  cggtts_cal_id: string;
  
  // Controller Configuration
  controller_type: 'kalman' | 'pi';
  pi_kp: number;
  pi_ki: number;
  kalman_window_size: number;

  // PI loop calculator parameters — persisted with the config but not sent to
  // the device. The derived pi_kp / pi_ki are what travel through MQTT.
  pi_tau_n_minutes: number;
  pi_zeta: number;
  pi_tau3_ratio: number;
  pi_fo_hz: number;
  pi_y_step_hz: number;

  // Optional fields
  notes?: string;
}

export interface DeviceConfig extends DeviceConfigBase {
  id: number;
  device_id: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  version: number;
  // is_adsb_enabled is returned by the API (auto-calculated) but not user-configurable
  is_adsb_enabled: boolean;
  // Level/preset rules this saved config violates (legacy rows); empty when valid
  preset_errors?: string[];
}

export interface DeviceConfigCreate extends DeviceConfigBase {}

export interface DeviceConfigUpdate extends Partial<DeviceConfigBase> {}

export interface DeviceConfigYAML {
  OCXO_CONTROL_TYPE: 'SPI' | 'SSH';
  PPS_OFFSET_NS: number;
  OCXO_CONTROL_BASELINE: number;
  STEERING_MODE: string;
  IS_ADSB_ENABLED: boolean;
  NUM_SLOTS: number;
  LEVEL: number;
  LATITUDE: number;
  LONGITUDE: number;
  HEIGHT_M: number;
  LATITUDE_ADSB: number;
  LONGITUDE_ADSB: number;
  HEIGHT_ADSB_M: number;
  OPENLOOP_COMMAND: number;
  GNSS_INPUTS: string[];
  GNSS_INTERNAL_DELAYS: number[];
  GNSS_CABLE_DELAYS: number[];
  ADSB_INTERNAL_DELAY: number;
  ADSB_CABLE_DELAY: number;
  EXT_REF_INTERNAL_DELAY: number;
  EXT_REF_CABLE_DELAY: number;
  KALMAN_CGGTTS_MODE: boolean;
  CGGTTS_REF_DLY: number;
  CGGTTS_CAB_DLY: number[];
  CGGTTS_INT_DLY: number[][];
  CGGTTS_CAL_ID: string;
  CONTROLLER_CONFIG: ControllerConfig;
}

export interface DeviceConfigHistory {
  configs: DeviceConfig[];
}

export interface ConfigDeployResponse {
  status: string;
  deployment_id: string;
  config_version: number;
  timestamp: string;
  message: string;
  mqtt_topic: string;
}

export interface DeploymentResult {
  deployment_id: string;
  device_id: number;
  config_version: number;
  status: 'pending' | 'in_progress' | 'success' | 'failed' | 'timeout' | 'cancelled';
  timestamp: string;
  message: string;
  mqtt_topic: string;
  attempts: number;
  error_details?: string;
}

export interface DeploymentHistoryResponse {
  device_id: number;
  deployments: DeploymentResult[];
}

export interface AllDeploymentsResponse {
  deployments: DeploymentResult[];
}

// Form validation rules
export const CONFIG_VALIDATION = {
  num_slots: { min: 1, max: 50 },
  gnss_count: { min: 1, max: 4 },
  kalman_window_size: { min: 1, max: 100 },
  pi_kp: { min: 0 },
  pi_ki: { min: 0 },
  pi_tau_n_minutes: { min: 0.001 },
  pi_zeta: { min: 0.01, max: 10 },
  pi_tau3_ratio: { min: 0, max: 1 },
  pi_fo_hz: { min: 1 },
  pi_y_step_hz: { min: 1e-12 },
  ocxo_control_baseline: { min: 0, max: 1048575 },
  cggtts_ref_dly: { min: -1000, max: 1000 },
  cggtts_delay: { min: 0, max: 1000 },
  cggtts_cal_id_max_length: 32,
  controller_types: ['kalman', 'pi'] as const,
  ocxo_control_types: ['SPI', 'SSH'] as const,
  notes_max_length: 1000
};

// GNSS descriptions for UI
export const GNSS_DESCRIPTIONS = [
  "GPS L1/L2",
  "GPS L1/L5", 
  "GAL E1/E5a",
  "GAL E1/E5b"
];

// CGGTTS signals of each receiver (GNSS1..4), same on every board. Mirrors
// constellation_type in modo_project/gnss_processor/constants.py: checked by
// tools/check_contract.py, keep the `constellation: '...'` literals.
export const RECEIVER_SIGNALS = [
  { constellation: 'gps_l1_l2', signals: ['GPS L1', 'GPS L2'] },
  { constellation: 'gps_l1_l5', signals: ['GPS L1', 'GPS L5'] },
  { constellation: 'galileo_e1_e5a', signals: ['GAL E1', 'GAL E5a'] },
  { constellation: 'galileo_e1_e5b', signals: ['GAL E1', 'GAL E5b'] },
] as const;

export const DEFAULT_CGGTTS_CAL_ID = 'NA';

export const isValidCalId = (value: string): boolean => /^\S{1,32}$/.test(value);

export const isInRange = (value: number, range: { min: number; max: number }): boolean =>
  Number.isFinite(value) && value >= range.min && value <= range.max;

// Steering mode options for UI
export const STEERING_MODES = [
  { value: 'external_reference', label: 'External Reference' },
  { value: 'single_channel_gnss', label: 'Single Channel GNSS' },
  { value: 'multi_channel_gnss', label: 'Multi Channel GNSS' },
  { value: 'adsb', label: 'ADS-B' },
  { value: 'gnss_adsb', label: 'GNSS + ADS-B' },
  { value: 'open_loop', label: 'Open Loop (Fixed Command)' },
] as const;

// Default delay values
export const DEFAULT_INTERNAL_DELAYS = [26.6371, 25.2, 26.2, 26.246];
export const DEFAULT_CABLE_DELAYS = [0.0, 0.0, 0.0, 0.0];

// Helper function to calculate is_adsb_enabled based on steering_mode
export const calculateIsAdsbEnabled = (steering_mode: DeviceConfigBase['steering_mode']): boolean => {
  return steering_mode === 'adsb' || steering_mode === 'gnss_adsb';
};

// Default configuration values
export const DEFAULT_CONFIG: DeviceConfigBase = {
  ocxo_control_type: "SPI",
  pps_offset_ns: 0,
  ocxo_control_baseline: 500000,
  steering_mode: "single_channel_gnss",
  // is_adsb_enabled is automatically calculated based on steering_mode
  num_slots: 5,
  level: 2,
  adsb_excluded_master_ids: [],
  latitude_gnss_ant: 44.42034947342054,
  longitude_gnss_ant: 7.547929586505649,
  height_gnss_ant_m: 0.0,
  latitude_adsb_ant: 44.42034947342054,
  longitude_adsb_ant: 7.547929586505649,
  height_adsb_ant_m: 0.0,
  openloop_command: 0,
  gnss_count: 1,
  gnss_internal_delays: [26.6371],
  gnss_cable_delays: [0.0],
  adsb_internal_delay: 0.0,
  adsb_cable_delay: 0.0,
  ext_ref_internal_delay: 0.0,
  ext_ref_cable_delay: 0.0,
  kalman_cggtts_mode: false,
  cggtts_ref_dly: 0.0,
  cggtts_cab_dly: [0.0],
  cggtts_int_dly: [[0.0, 0.0]],
  cggtts_cal_id: DEFAULT_CGGTTS_CAL_ID,
  controller_type: "kalman",
  pi_kp: 0.000244140625,
  pi_ki: 0.0000076293945,
  kalman_window_size: 10,
  pi_tau_n_minutes: 1.0,
  pi_zeta: 1.0,
  pi_tau3_ratio: 1 / 6,
  pi_fo_hz: 1e7,
  pi_y_step_hz: 6.6736e-6,
  notes: ""
};
// True when any CGGTTS field is outside the ranges the server enforces.
export const hasInvalidCggtts = (data: DeviceConfigBase): boolean => {
  const delayOk = (v: number) => isInRange(v, CONFIG_VALIDATION.cggtts_delay);
  if (!isInRange(data.cggtts_ref_dly, CONFIG_VALIDATION.cggtts_ref_dly)) return true;
  if (!(data.cggtts_cab_dly ?? []).every(delayOk)) return true;
  if (!(data.cggtts_int_dly ?? []).every((p) => Array.isArray(p) && p.length === 2 && p.every(delayOk))) return true;
  return !isValidCalId(data.cggtts_cal_id);
};
